import { PIVOT_DIRECTIONS } from "@/constants/base"
import type { PivotDirection } from "@/constants/base"
import { isNumberValid, makeValidator } from "./type-guards"

import type * as Models from "@/redux/models"

export interface PivotActivityStartAndEndValues {
  activityStartAzimuth: number
  currentDirection: PivotDirection
  mostRecentValidAzimuth: number
}

export interface PivotActivityMetrics {
  /** Current speed in hours per revolution (null if not moving/available) */
  hoursPerRevolution: number | null
  /** Linear speed at the outer edge in meters per hour */
  metersPerHour: number | null
  /** Distance to the next stop in meters */
  distanceToNextStopMeters: number | null
  /** Expected timestamp when pivot will reach next stop */
  nextStopExpectedTimestamp: string | null
  /** Name/label of the next stop */
  nextStopLabel: string | null
  /** Current pivot heading in degrees (0-360, 0=North) */
  currentHeadingDegrees: number | null
  /** Current direction of movement */
  direction: PivotDirection | null
  /** Percent complete to next stop (0-100) */
  percentComplete: number | null
}

const isPivotDirection = makeValidator(PIVOT_DIRECTIONS)

export function isActivityFinished(
  activity: Models.DeviceActivity | null | undefined,
): boolean {
  if (!activity) return false
  if (activity.isMarkedFinished === true) return true
  if (typeof activity.finishReason === "string" && activity.finishReason.length > 0) return true
  if (typeof activity.finishDate === "string") {
    const finishMs = Date.parse(activity.finishDate)
    if (Number.isFinite(finishMs) && finishMs <= Date.now()) return true
  }
  return false
}

/**
 * Get the most recent non-null value from an activity array
 */
export function getLatestValue<T>(
  array: Array<T | null | undefined> | null | undefined,
): T | null {
  if (!array) return null
  for (let i = array.length - 1; i >= 0; i--) {
    const value = array[i]
    if (value !== null && value !== undefined) {
      return value
    }
  }
  return null
}

/**
 * Extract pivot activity metrics from device activity data.
 * Returns the most recent valid values for speed, direction, time to stop, etc.
 */
export function getPivotActivityMetrics(
  activity: Models.DeviceActivity | null | undefined,
): PivotActivityMetrics {
  // Once an activity is finished, its trailing array values (speed, percent-
  // complete, direction) are stale snapshots — surfacing them in the UI makes
  // it look like the pivot is still moving. Treat any finished activity the
  // same as no activity. We accept any of three signals because backfills /
  // admin edits don't always touch all three fields together:
  //   - isMarkedFinished===true (set by mark_finished)
  //   - finishReason set        (set by mark_finished or admin)
  //   - finishDate <= now       (the future date set by _increment_finish_date
  //                              has elapsed without a state-change update)
  if (!activity || isActivityFinished(activity)) {
    return {
      hoursPerRevolution: null,
      metersPerHour: null,
      distanceToNextStopMeters: null,
      nextStopExpectedTimestamp: null,
      nextStopLabel: null,
      currentHeadingDegrees: null,
      direction: null,
      percentComplete: null,
    }
  }

  const hoursPerRevolution = getLatestValue(activity.pivotSpeedsHoursPerRevolution)
  const metersPerHour = getLatestValue(activity.travelerSpeedsMetersPerHour)
  const distanceToNextStopMeters = getLatestValue(activity.travelerDistancesMetersToNextStop)
  const nextStopTimestamp = getLatestValue(activity.travelerNextStopExpectedTimestamps)
  const nextStopLabel = getLatestValue(activity.travelerNextStops ?? null)
  const currentHeadingDegrees = getLatestValue(activity.pivotHeadingsDegrees)
  const directionValue = getLatestValue(activity.travelerDirections ?? null)
  const direction = isPivotDirection(directionValue) ? directionValue : null
  const percentCompleteRaw = getLatestValue(activity.travelerPercentCompletes ?? null)
  // Backend DecimalField serializes as string (e.g., "75.00"), so parse it
  const percentComplete = typeof percentCompleteRaw === "string"
    ? parseFloat(percentCompleteRaw)
    : percentCompleteRaw

  return {
    hoursPerRevolution: isNumberValid(hoursPerRevolution) ? hoursPerRevolution : null,
    metersPerHour: isNumberValid(metersPerHour) ? metersPerHour : null,
    distanceToNextStopMeters: isNumberValid(distanceToNextStopMeters) ? distanceToNextStopMeters : null,
    nextStopExpectedTimestamp: typeof nextStopTimestamp === "string" ? nextStopTimestamp : null,
    nextStopLabel: typeof nextStopLabel === "string" ? nextStopLabel : null,
    currentHeadingDegrees: isNumberValid(currentHeadingDegrees) ? currentHeadingDegrees : null,
    direction,
    percentComplete: isNumberValid(percentComplete) ? percentComplete : null,
  }
}

/**
 * Last observed arm heading, ignoring the finished-activity gate.
 * Unlike speed/direction, the arm's *position* stays valid after a run
 * ends — the pivot doesn't move when it isn't running.
 */
export function getLastKnownPivotHeading(
  activity: Models.DeviceActivity | null | undefined,
): number | null {
  const heading = getLatestValue(activity?.pivotHeadingsDegrees ?? null)
  return isNumberValid(heading) ? heading : null
}

/**
 * Format hours per revolution into a human-readable duration string.
 * Examples:
 *   - 1.5 -> "1h 30m"
 *   - 0.5 -> "30m"
 *   - 24 -> "24h 0m" or "1d 0h"
 *   - 72.5 -> "3d 0h 30m"
 */
export function formatHoursPerRevolution(
  hoursPerRevolution: number | null | undefined,
): string | null {
  if (!isNumberValid(hoursPerRevolution) || hoursPerRevolution <= 0) {
    return null
  }

  const totalMinutes = Math.round(hoursPerRevolution * 60)
  const days = Math.floor(totalMinutes / (24 * 60))
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60)
  const minutes = totalMinutes % 60

  if (days > 0) {
    if (minutes > 0) {
      return `${days}d ${hours}h ${minutes}m`
    }
    return `${days}d ${hours}h`
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  return `${minutes}m`
}

/**
 * Calculate time remaining to next stop from the expected timestamp.
 * Returns milliseconds remaining, or null if no valid timestamp.
 */
export function getTimeToNextStopMs(
  nextStopExpectedTimestamp: string | null | undefined,
  now?: Date,
): number | null {
  if (!nextStopExpectedTimestamp) return null

  const targetTime = new Date(nextStopExpectedTimestamp).getTime()
  if (isNaN(targetTime)) return null

  const currentTime = (now ?? new Date()).getTime()
  const remaining = targetTime - currentTime

  // Return null if the time has passed
  return remaining > 0 ? remaining : null
}

/**
 * Format milliseconds into a duration string.
 * Examples:
 *   - 3600000 (1 hour) -> "1h 0m"
 *   - 5400000 (1.5 hours) -> "1h 30m"
 *   - 180000 (3 minutes) -> "3m"
 */
export function formatDurationMs(milliseconds: number | null | undefined): string | null {
  if (!isNumberValid(milliseconds) || milliseconds <= 0) {
    return null
  }

  const totalMinutes = Math.round(milliseconds / 60000)
  const days = Math.floor(totalMinutes / (24 * 60))
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60)
  const minutes = totalMinutes % 60

  if (days > 0) {
    if (minutes > 0 && hours === 0) {
      return `${days}d ${minutes}m`
    }
    if (hours > 0) {
      return `${days}d ${hours}h`
    }
    return `${days}d`
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  if (minutes > 0) {
    return `${minutes}m`
  }
  return "< 1m"
}

/**
 * Calculate time to complete a full revolution based on current speed.
 * @param hoursPerRevolution - Speed in hours per revolution
 * @param percentComplete - Current completion percentage (0-100), used to calculate remaining time
 * @returns Milliseconds to complete the revolution, or null if invalid input
 */
export function getTimeToCompleteRevolutionMs(
  hoursPerRevolution: number | null | undefined,
  percentComplete?: number | null,
): number | null {
  if (!isNumberValid(hoursPerRevolution) || hoursPerRevolution <= 0) {
    return null
  }

  // If we have percent complete, calculate remaining time
  if (isNumberValid(percentComplete) && percentComplete >= 0 && percentComplete <= 100) {
    const remainingPercent = 100 - percentComplete
    return (hoursPerRevolution * remainingPercent / 100) * 60 * 60 * 1000
  }

  // Otherwise return full revolution time
  return hoursPerRevolution * 60 * 60 * 1000
}

/**
 * Given a device activity, return the azimuth values for the start and end of
 * the pivot activity, as well as the direction. This ensures that
 * the 'sector' we draw represents a continues movement in a single direction,
 * because currently, the pivot activity data can have direction changes.
 */
export function getSectorDrawingValuesForPivotActivity(
  activity: Pick<
    Models.DeviceActivity,
    "pivotHeadingsDegrees" | "travelerDirections"
  >,
): PivotActivityStartAndEndValues | undefined {
  let currentDirection: PivotDirection | undefined
  let mostRecentValidAzimuth: number | undefined
  let activityStartAzimuth: number | undefined
  let hasAzimuthChanged = false

  const azimuths = activity.pivotHeadingsDegrees ?? []
  const directions = activity.travelerDirections ?? []

  for (let index = azimuths.length - 1; index >= 0; index--) {
    const directionNext = directions[index]

    // If we have not yet determined the most recent direction, set it
    if (!currentDirection && isPivotDirection(directionNext)) {
      currentDirection = directionNext
    }

    // If the direction changes, stop iterating
    if (currentDirection && directionNext !== currentDirection) {
      break
    }

    // If we don't yet know the direction, don't collect azimuths
    if (!currentDirection) {
      continue
    }
    const azimuthNext = azimuths[index]

    // If the azimuth is null, keep looking
    if (typeof azimuthNext !== "number") {
      continue
    }

    // If we have not determined the azimuth at the 'end' of the activity set it
    if (typeof mostRecentValidAzimuth === "undefined") {
      mostRecentValidAzimuth = azimuthNext
    } else {
      if (azimuthNext !== mostRecentValidAzimuth) {
        hasAzimuthChanged = true
      }

      // If the pivot has just been sitting there, we don't want to
      // return it since it has not actually gone in a circle
      if (hasAzimuthChanged) {
        activityStartAzimuth = azimuthNext
      }
    }
  }

  // Check results to make sure they are all defined and valid
  if (
    typeof mostRecentValidAzimuth === "number" &&
    typeof activityStartAzimuth === "number" &&
    isPivotDirection(currentDirection)
  ) {
    return {
      activityStartAzimuth,
      currentDirection,
      mostRecentValidAzimuth,
    }
  }
  return undefined
}

// Speed conversion constants
// Range: 1 hour/rev (fastest, 100%) to 24 hours/rev (slowest, ~0%)
const MIN_HOURS_PER_REV = 1
const MAX_HOURS_PER_REV = 24

/**
 * Convert hours per revolution to a speed percentage (0-100).
 * Lower hours = faster = higher percentage.
 *
 * Range mapping:
 * - 1 hour/rev → 100% (fastest)
 * - 24 hours/rev → 0% (slowest)
 *
 * @param hoursPerRevolution - Speed in hours per revolution
 * @returns Speed percentage 0-100, or null if invalid input
 */
export function hoursPerRevolutionToSpeedPercent(
  hoursPerRevolution: number | null | undefined,
): number | null {
  if (!isNumberValid(hoursPerRevolution) || hoursPerRevolution <= 0) {
    return null
  }

  // Clamp to valid range
  const clamped = Math.max(MIN_HOURS_PER_REV, Math.min(MAX_HOURS_PER_REV, hoursPerRevolution))

  // Linear interpolation: 1 hour = 100%, 24 hours = 0%
  const percent = 100 * (1 - (clamped - MIN_HOURS_PER_REV) / (MAX_HOURS_PER_REV - MIN_HOURS_PER_REV))

  return Math.round(Math.max(0, Math.min(100, percent)))
}

/**
 * Convert speed percentage (0-100) to hours per revolution.
 *
 * Range mapping:
 * - 100% → 1 hour/rev (fastest)
 * - 0% → 24 hours/rev (slowest)
 *
 * @param percent - Speed percentage 0-100
 * @returns Hours per revolution
 */
export function speedPercentToHoursPerRevolution(percent: number): number {
  // Clamp to valid range
  const clamped = Math.max(0, Math.min(100, percent))

  // Linear interpolation: 100% = 1 hour, 0% = 24 hours
  return MIN_HOURS_PER_REV + (MAX_HOURS_PER_REV - MIN_HOURS_PER_REV) * (1 - clamped / 100)
}

/**
 * Determine if a pivot is currently moving based on activity metrics.
 * A pivot is considered moving if it has a valid direction and speed.
 */
export function isPivotMoving(metrics: PivotActivityMetrics): boolean {
  return (
    metrics.direction !== null &&
    metrics.hoursPerRevolution !== null &&
    metrics.hoursPerRevolution > 0
  )
}

/**
 * Determine if pivot is moving clockwise based on activity metrics.
 */
export function isPivotClockwise(metrics: PivotActivityMetrics): boolean {
  return metrics.direction === "pivot_clockwise"
}
